import { useMutation } from "@tanstack/react-query";
import { MoreHorizontal } from "lucide-react";
import { useState } from "react";
import { useTranslations } from "use-intl";

import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@lawline/ui/components/alert-dialog";
import { Button } from "@lawline/ui/components/button";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@lawline/ui/components/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "@lawline/ui/components/tooltip";

import { setTenantPlanMutation } from "../tenants.mutations";
import type { AdminTenant } from "../tenants.types";

// The tier sets the monthly quota, so a dialog confirms the switch.
export function TenantPlanActions({ tenant }: { tenant: AdminTenant }) {
	const t = useTranslations("admin.tenants.actions");
	const tActions = useTranslations("common.actions");
	const setPlan = useMutation(setTenantPlanMutation);
	const [confirmOpen, setConfirmOpen] = useState(false);

	const isEnterprise = tenant.rateTier === "enterprise";
	const nextTier = isEnterprise ? "standard" : "enterprise";

	return (
		<>
			<DropdownMenu>
				<Tooltip>
					<TooltipTrigger asChild>
						<DropdownMenuTrigger asChild>
							<Button variant="ghost" size="icon-xs">
								<MoreHorizontal />
							</Button>
						</DropdownMenuTrigger>
					</TooltipTrigger>
					<TooltipContent>{tActions("more")}</TooltipContent>
				</Tooltip>
				<DropdownMenuContent align="end">
					<DropdownMenuItem onClick={() => setConfirmOpen(true)}>
						{isEnterprise ? t("downgrade") : t("upgrade")}
					</DropdownMenuItem>
				</DropdownMenuContent>
			</DropdownMenu>

			<AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>
							{t("confirmTitle", {
								name: tenant.name,
								plan: t(`plans.${nextTier}`),
							})}
						</AlertDialogTitle>
						<AlertDialogDescription>{t("confirmDescription")}</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>{t("confirmCancel")}</AlertDialogCancel>
						<AlertDialogAction
							onClick={() => setPlan.mutate({ id: tenant.id, rateTier: nextTier })}
						>
							{t("confirmConfirm")}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</>
	);
}
